import React, { useRef, useEffect, useState } from "react";
import { RotateCcw, X } from "lucide-react";
import SlidePanel from "../add_customer/SlidePanel";
import { toast } from "react-toastify";

const EditIdentity = ({
  id_R,
  closeIDlModal,
  isIDModalOpen,
  identificationType,
  setIdentificationType,
  country,
  setCountry,
  frontFile,
  setFrontFile,
  backFile,
  setBackFile,
  passportFile,
  setPassportFile,
  isContinueEnabled,
  setIsContinueEnabled,
  setIsContinueModalOpen,
  customer,
  uploadKenyaIdBack,
  uploadKenyaIdFront,
  uploadPassport,
}) => {
  const handleBackButtonClick = () => {
    // Close the current modal
    closeIDlModal();

    setFrontFile(null);
    setBackFile(null);
    setPassportFile(null);
  };

  const handleNextButtonClick = () => {
    setIsContinueModalOpen(true);
  };
  const [isBackUploading, setIsBackUploading] = useState(false);
  const [backStatus, setBackStatus] = useState(null);
  const fileInputFront = useRef(null);
  const fileInputBack = useRef(null);
  const fileInputPassport = useRef(null);

  useEffect(() => {
    if (identificationType === "National Id") {
      setIsContinueEnabled(frontFile !== null && backFile !== null);
    } else if (identificationType === "passport") {
      setIsContinueEnabled(passportFile !== null);
    } else {
      setIsContinueEnabled(false);
    }
  }, [identificationType, frontFile, backFile, passportFile]);

  const openFrontFilePicker = () => {
    fileInputFront.current.click();
  };

  const [isFrontUploading, setIsFrontUploading] = useState(false);

  const handleFrontFileChange = async (e) => {
    const file = e?.target?.files[0];

    if (!file) return;

    setIsFrontUploading(true);

    try {
      const res = await uploadKenyaIdFront(file);

      if (!validateFrontId(res)) {
        toast?.error("Invalid Front ID. Upload the front side of the ID.", {
          autoClose: false,
        });

        return;
      }

      setFrontFile(file);
    } catch (err) {
      toast?.error("Please upload a clear front side of a valid National ID.", {
        autoClose: false,
      });
    } finally {
      setIsFrontUploading(false);
    }
  };

  const openBackFilePicker = () => {
    fileInputBack.current.click();
  };

  const validateBackId = (res) => {
    const data = res?.data;

    return data?.foundAny === true;
  };

  const validateFrontId = (res) => {
    const data = res?.data;

    return Boolean(
      data?.idNumber && data?.fullNames && data?.dateOfBirth && data?.sex,
    );
  };

  const validatePassport = (res) => {
    const data = res?.data;

    return Boolean(data?.passportNumber && data?.fullName && data?.dateOfBirth);
  };

  const handleBackFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsBackUploading(true);
    setBackStatus(null);

    try {
      const res = await uploadKenyaIdBack(file);

      if (!validateBackId(res)) {
        setBackStatus("error");
        toast?.error("Invalid Back ID. Upload the back side of the ID.", {
          autoClose: false,
        });
        return;
      }

      setBackFile(file);
      setBackStatus("success");
    } catch (err) {
      setBackStatus("error");
      toast?.error(err?.message || "Validation failed.", {
        autoClose: false,
      });
    } finally {
      setIsBackUploading(false);
    }
  };

  const openPassportFilePicker = () => {
    fileInputPassport?.current?.click();
  };

  const [isPassportUploading, setIsPassportUploading] = useState(false);

  const handlePassportFileChange = async (e) => {
    const file = e?.target?.files[0];

    if (!file) return;

    setIsPassportUploading(true);

    try {
      const res = await uploadPassport(file);

      if (!validatePassport(res)) {
        toast?.error("Invalid Passport. Upload a clear valid passport image.", {
          autoClose: false,
        });

        return;
      }

      setPassportFile(file);
    } catch (err) {
      toast?.error("Please upload a clear valid passport image.", {
        autoClose: false,
      });
    } finally {
      setIsPassportUploading(false);
    }
  };

  const truncateFileName = (fileName) => {
    const maxLength = 12;
    const extensionIndex = fileName.lastIndexOf(".");
    const nameWithoutExtension = fileName.substring(0, extensionIndex);
    const truncatedName =
      nameWithoutExtension.length > maxLength
        ? `${nameWithoutExtension.substring(0, maxLength)}...`
        : nameWithoutExtension;
    return `${truncatedName}.${fileName.substring(extensionIndex + 1)}`;
  };

  const handleCloseSelfieModal = () => {
    closeIDlModal();

    setFrontFile(null);
    setBackFile(null);
    setPassportFile(null);
  };

  const handleReplaceFile = (type) => {
    switch (type) {
      case "front":
        fileInputFront.current?.click();
        break;

      case "back":
        fileInputBack.current?.click();
        break;

      case "passport":
        fileInputPassport.current?.click();
        break;

      default:
        break;
    }
  };

  return (
    <SlidePanel isOpen={isIDModalOpen} onClose={handleCloseSelfieModal}>
      <div className="relative flex flex-col h-full bg-white rounded-[12px]">
        {/* Close Button */}
        <div
          onClick={handleCloseSelfieModal}
          className="absolute top-0 right-0 cursor-pointer h-[40px] w-[40px] bg-[#FF0000] rounded-tr-[10px] rounded-bl-[20px] flex items-center justify-center z-10"
        >
          <X color="#ffffff" />
        </div>

        <div className="flex flex-col h-full">
          <div className="mt-5 px-5 mx-auto items-center">
            {customer?.id_back_image === null &&
            customer?.id_front_image === null ? (
              <>
                <p className="text-[#000] text-xl font-bold">
                  Upload National ID
                </p>
                <div className="text-[#000] mt-5 text-[15px] font-[300]">
                  <p>
                    Please upload identity documents to verify the Member's
                    identity and personal details. Ensure that the provided
                    identity document clearly matches the Member's face.
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#000] text-xl font-bold">
                  Replace document
                </p>
                <div className="text-[#000] mt-[30px] text-[15px] font-[300]">
                  <p>
                    ⚠️ Use this option if the user has provided a new or
                    corrected version of their National ID. Replacing the
                    document will permanently delete the existing file from the
                    system. Ensure the new document is authentic and properly
                    verified before proceeding.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-5 pb-6 mt-2">
            <div className="flex w-full flex-col justify-center mt-5">
              <label
                htmlFor="country"
                className="mb-1 text-md font-normal text-[#000]"
              >
                Country of citizenship
              </label>
              <select
                name="county"
                id="county"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-[99%] h-10 px-4 rounded-[4px] bg-[#EAEAEA] border-[1px] border-[#EAEAEA]"
              >
                <option value="">Select county</option>
                <option value="Kenya">Kenya</option>
                <option value="United States">United States</option>
              </select>
            </div>
            <div className="flex w-full flex-col justify-center mt-5">
              <label
                htmlFor="county"
                className="mb-1 text-md font-normal text-[#000]"
              >
                Select Identity document type
              </label>
              <select
                name="Idtype"
                id="Idtype"
                value={identificationType}
                onChange={(e) => setIdentificationType(e.target.value)}
                className="w-[99%] bg-[#EAEAEA] h-10 px-4 border-[1px] rounded-[4px] border-[#EAEAEA]"
              >
                <option value="">Select county</option>
                <option value="National Id">National Id</option>
                <option value="passport">Passport</option>
              </select>
            </div>

            {identificationType === "National Id" && (
              <div className="flex flex-col items-center mt-[40px]">
                <div className="w-full">
                  <p className="text-left text-[16px] mb-2 text-[#000]">
                    Upload document (Front page of ID)
                  </p>
                  <div
                    onClick={
                      !isFrontUploading ? openFrontFilePicker : undefined
                    }
                    className={`w-full h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer ${
                      isFrontUploading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <svg
                      width="45"
                      height="45"
                      viewBox="0 0 45 45"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.5625 29.2069V12.9881L17.1938 17.3569L15.8663 16.0106L22.5 9.375L29.1356 16.0106L27.8081 17.3588L23.4375 12.9881V29.2069H21.5625ZM12.405 35.625C11.5413 35.625 10.8206 35.3363 10.2431 34.7588C9.66562 34.1812 9.37625 33.46 9.375 32.595V28.0519H11.25V32.595C11.25 32.8838 11.37 33.1488 11.61 33.39C11.85 33.6313 12.1144 33.7512 12.4031 33.75H32.5969C32.8844 33.75 33.1488 33.63 33.39 33.39C33.6313 33.15 33.7512 32.885 33.75 32.595V28.0519H35.625V32.595C35.625 33.4588 35.3363 34.1794 34.7588 34.7569C34.1812 35.3344 33.46 35.6238 32.595 35.625H12.405Z"
                        fill="black"
                      />
                    </svg>
                    <input
                      type="file"
                      ref={fileInputFront}
                      onChange={handleFrontFileChange}
                      style={{ display: "none" }}
                    />

                    <p className="text-#074073B8 text-16 text-center">
                      {!frontFile ? (
                        <>
                          Drag and Drop or{" "}
                          <span style={{ color: "#0187E8" }}>Choose file</span>{" "}
                          here
                        </>
                      ) : (
                        truncateFileName(frontFile?.name)
                      )}
                    </p>
                    {isFrontUploading && (
                      <p className="text-sm text-[#074073] mt-2">
                        Validating Front ID...
                      </p>
                    )}

                    {frontFile && (
                      <div className="flex flex-col items-center mt-2">
                        <p className="text-[#074073B8] w-90% truncate text-16 text-center">
                          {frontFile.type} ({(frontFile.size / 1024).toFixed(2)}{" "}
                          KB)
                        </p>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReplaceFile("front");
                          }}
                          className="mt-3 flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[#E6F0FF] hover:bg-[#D9E9FF] text-[#000000] transition-all duration-200"
                        >
                          <RotateCcw size={16} />
                          <span className="text-sm font-medium">
                            Replace File
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full mt-5">
                  <p className="text-left text-[16px] mb-2 text-[#000]">
                    Upload document (back page of ID)
                  </p>
                  <div
                    // onClick={openBackFilePicker}
                    // className="w-[94%] h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer"
                    onClick={!isBackUploading ? openBackFilePicker : undefined}
                    className={`w-full h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer ${backStatus === "success" ? "bg-[#EAEAEA] border-[1px] border-[#000000]" : ""} ${backStatus === "error" ? "bg-[#EAEAEA] border-[1px] border-[#000000]" : ""} ${isBackUploading ? "opacity-60 cursor-not-allowed" : ""} `}
                  >
                    <svg
                      width="45"
                      height="45"
                      viewBox="0 0 45 45"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.5625 29.2069V12.9881L17.1938 17.3569L15.8663 16.0106L22.5 9.375L29.1356 16.0106L27.8081 17.3588L23.4375 12.9881V29.2069H21.5625ZM12.405 35.625C11.5413 35.625 10.8206 35.3363 10.2431 34.7588C9.66562 34.1812 9.37625 33.46 9.375 32.595V28.0519H11.25V32.595C11.25 32.8838 11.37 33.1488 11.61 33.39C11.85 33.6313 12.1144 33.7512 12.4031 33.75H32.5969C32.8844 33.75 33.1488 33.63 33.39 33.39C33.6313 33.15 33.7512 32.885 33.75 32.595V28.0519H35.625V32.595C35.625 33.4588 35.3363 34.1794 34.7588 34.7569C34.1812 35.3344 33.46 35.6238 32.595 35.625H12.405Z"
                        fill="black"
                      />
                    </svg>

                    <input
                      type="file"
                      ref={fileInputBack}
                      onChange={handleBackFileChange}
                      style={{ display: "none" }}
                    />

                    <p className="text-#074073B8 text-16 text-center">
                      {!backFile ? (
                        <>
                          Drag and Drop or{" "}
                          <span style={{ color: "#0187E8" }}>Choose file</span>{" "}
                          here
                        </>
                      ) : (
                        truncateFileName(backFile?.name)
                      )}
                    </p>

                    {isBackUploading && (
                      <p className="text-sm text-[#074073] mt-2">
                        Validating Back ID...
                      </p>
                    )}

                    {backFile && (
                      <div className="flex flex-col items-center mt-2">
                        <p className="text-[#074073B8] text-16">
                          {backFile?.type} (
                          {(backFile?.size / 1024)?.toFixed(2)} KB)
                        </p>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReplaceFile("back");
                          }}
                          className="mt-3 flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[#E6F0FF] hover:bg-[#D9E9FF] text-[#074073] transition-all duration-200"
                        >
                          <RotateCcw size={16} />
                          <span className="text-sm font-medium">
                            Replace File
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {identificationType === "passport" && (
              <div className="flex items-center mt-10">
                <div className="w-full">
                  <p className="text-left text-[16px] mb-2 text-[#000]">
                    Upload document (Should capture bio data page & first page)
                  </p>
                  <div
                    onClick={
                      !isPassportUploading ? openPassportFilePicker : undefined
                    }
                    className={`w-full h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer ${
                      isPassportUploading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <svg
                      width="45"
                      height="45"
                      viewBox="0 0 45 45"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.5625 29.2069V12.9881L17.1938 17.3569L15.8663 16.0106L22.5 9.375L29.1356 16.0106L27.8081 17.3588L23.4375 12.9881V29.2069H21.5625ZM12.405 35.625C11.5413 35.625 10.8206 35.3363 10.2431 34.7588C9.66562 34.1812 9.37625 33.46 9.375 32.595V28.0519H11.25V32.595C11.25 32.8838 11.37 33.1488 11.61 33.39C11.85 33.6313 12.1144 33.7512 12.4031 33.75H32.5969C32.8844 33.75 33.1488 33.63 33.39 33.39C33.6313 33.15 33.7512 32.885 33.75 32.595V28.0519H35.625V32.595C35.625 33.4588 35.3363 34.1794 34.7588 34.7569C34.1812 35.3344 33.46 35.6238 32.595 35.625H12.405Z"
                        fill="black"
                      />
                    </svg>
                    <input
                      type="file"
                      ref={fileInputPassport}
                      onChange={handlePassportFileChange}
                      style={{ display: "none" }}
                    />

                    <p className="text-#074073B8 text-16 text-center">
                      {!passportFile ? (
                        <>
                          Drag and Drop or{" "}
                          <span style={{ color: "#0187E8" }}>Choose file</span>{" "}
                          here
                        </>
                      ) : (
                        truncateFileName(passportFile?.name)
                      )}
                    </p>

                    {isPassportUploading && (
                      <p className="text-sm text-[#074073] mt-2">
                        Validating Passport...
                      </p>
                    )}

                    {passportFile && (
                      <div className="flex flex-col items-center mt-2">
                        <p className="text-[#074073B8] w-90% truncate text-16 text-center">
                          {passportFile?.type} (
                          {(passportFile?.size / 1024)?.toFixed(2)} KB)
                        </p>

                        <button
                          type="button"
                          onClick={(e) => {
                            e?.stopPropagation();
                            handleReplaceFile("passport");
                          }}
                          className="mt-3 flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[#E6F0FF] hover:bg-[#D9E9FF] text-[#074073] transition-all duration-200"
                        >
                          <RotateCcw size={16} />
                          <span className="text-sm font-medium">
                            Replace File
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4">
            {customer?.id_back_image === null &&
            customer?.id_front_image === null ? (
              <div className="flex w-full items-end justify-end">
                <button
                  onClick={handleNextButtonClick}
                  data-modal-toggle={id_R}
                  type="button"
                  className={`text-white hover:bg-[#074073] focus:outline-none font-medium w-full rounded text-sm px-8 py-2.5 text-center ${
                    isContinueEnabled
                      ? "bg-[#074073]"
                      : "bg-[#D9D9D9] cursor-not-allowed"
                  }`}
                  disabled={!isContinueEnabled}
                >
                  Upload
                </button>
              </div>
            ) : (
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center justify-between w-[15vw]">
                  <button
                    onClick={handleBackButtonClick}
                    data-modal-toggle={id_R}
                    type="button"
                    className="text-[#000] bg-[#7FB8EB] hover:bg-[#7FB8EB] focus:outline-none font-medium rounded-[5px] text-sm px-8 py-2.5 text-center"
                  >
                    Back
                  </button>
                </div>
                <button
                  onClick={handleNextButtonClick}
                  data-modal-toggle={id_R}
                  type="button"
                  className={`text-white hover:bg-[#074073] focus:outline-none font-medium rounded-[5px] text-sm px-8 py-2.5 text-center ${
                    isContinueEnabled
                      ? "bg-[#074073]"
                      : "bg-[#D9D9D9] cursor-not-allowed"
                  }`}
                  disabled={!isContinueEnabled}
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SlidePanel>
  );
};

export default EditIdentity;
